import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

const mp = (path) => client.get(`/marketplace${path}`)
const mpPost = (path, data) => client.post(`/marketplace${path}`, data)
const mpPut = (path, data) => client.put(`/marketplace${path}`, data)
const mpDel = (path) => client.delete(`/marketplace${path}`)

export const useCartStore = defineStore('cart', () => {
  const cart = ref(null)
  const carts = ref([])
  const loading = ref(false)
  const error = ref(null)

  async function create(data) {
    loading.value = true
    try {
      const { data: result } = await mpPost('/carts', data)
      cart.value = result
      return result
    } catch (e) { error.value = e.message; throw e } finally { loading.value = false }
  }

  async function get(cartId) {
    loading.value = true
    try {
      const { data } = await mp(`/carts/${cartId}`)
      cart.value = data
      return data
    } catch (e) { error.value = e.message; throw e } finally { loading.value = false }
  }

  async function getByUser(userId) {
    loading.value = true
    try {
      const { data } = await mp(`/carts/user/${userId}`)
      carts.value = Array.isArray(data) ? data : [data]
      return carts.value
    } catch { return [] } finally { loading.value = false }
  }

  async function getByShareToken(shareTokenHash) {
    try { const { data } = await mp(`/carts/share/${shareTokenHash}`); cart.value = data; return data } catch { return null }
  }

  async function update(cartId, data) {
    try { const { data: result } = await mpPut(`/carts/${cartId}`, data); cart.value = result; return result } catch (e) { error.value = e.message; throw e }
  }

  async function remove(cartId) {
    try { await mpDel(`/carts/${cartId}`); cart.value = null } catch (e) { error.value = e.message; throw e }
  }

  async function checkout(cartId) {
    try { const { data } = await mpPost(`/carts/${cartId}/checkout`); cart.value = data; return data } catch (e) { error.value = e.message; throw e }
  }

  async function complete(cartId) {
    try { const { data } = await mpPost(`/carts/${cartId}/complete`); cart.value = data; return data } catch (e) { error.value = e.message; throw e }
  }

  async function cancel(cartId) {
    try { const { data } = await mpPost(`/carts/${cartId}/cancel`); cart.value = data; return data } catch (e) { error.value = e.message; throw e }
  }

  // ─── Cart Items ─────────────────────────────────────────────────

  async function addItem(cartId, data) {
    try { const { data: result } = await mpPost(`/carts/${cartId}/items`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function listItems(cartId) {
    try { const { data } = await mp(`/carts/${cartId}/items`); return data } catch { return [] }
  }

  async function updateItem(itemId, data) {
    try { const { data: result } = await mpPut(`/carts/items/${itemId}`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function removeItem(itemId) {
    try { await mpDel(`/carts/items/${itemId}`) } catch (e) { error.value = e.message; throw e }
  }

  // ─── Cart Participants ──────────────────────────────────────────

  async function addParticipant(cartId, data) {
    try { const { data: result } = await mpPost(`/carts/${cartId}/participants`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function listParticipants(cartId) {
    try { const { data } = await mp(`/carts/${cartId}/participants`); return data } catch { return [] }
  }

  async function updateParticipant(participantId, data) {
    try { const { data: result } = await mpPut(`/carts/participants/${participantId}`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function removeParticipant(participantId) {
    try { await mpDel(`/carts/participants/${participantId}`) } catch (e) { error.value = e.message; throw e }
  }

  async function addParticipantItem(participantId, data) {
    try { const { data: result } = await mpPost(`/carts/participants/${participantId}/items`, data); return result } catch (e) { error.value = e.message; throw e }
  }

  async function listParticipantItems(participantId) {
    try { const { data } = await mp(`/carts/participants/${participantId}/items`); return data } catch { return [] }
  }

  return {
    cart, carts, loading, error,
    create, get, getByUser, getByShareToken, update, remove, checkout,
    complete, cancel, addItem, listItems, updateItem, removeItem,
    addParticipant, listParticipants, updateParticipant, removeParticipant,
    addParticipantItem, listParticipantItems,
  }
})
